/**
 * Unit-aware formatting for status metrics (REQ-STATUS-075; feature spec
 * ``system-status-trend-detail`` §2.3, §9.1).
 *
 * The metric catalog has carried a ``unit`` beside ``label``, ``tooltip`` and
 * ``threshold`` since REQ-STATUS-050, and the UI **fetched it and threw it
 * away** — nothing formatted by it, so an unused knob stayed unused
 * (CLAUDE.md §10). The trend detail chart is the first consumer: an axis
 * cannot be labelled before its unit is available, and a value read off a
 * crosshair means nothing without one.
 *
 * # Six units are magnitudes and two are not
 *
 * ``count``, ``bytes``, ``percent``, ``seconds``, ``milliseconds`` and
 * ``cores`` differ only in how they are *written*; each is a quantity that can
 * sit on a min/mid/max axis. The other two cannot, and :func:`unitKind` exists
 * to keep them off it:
 *
 *   - **``boolean``** on a magnitude axis is a two-valued line pretending to be
 *     continuous. It is drawn as a band, because a boolean over time is a
 *     *state* and a band is what draws a state.
 *   - **``epoch_seconds``** renders ten-digit y ticks and a staircase climbing
 *     to the present — honest and useless. It is plotted as an **age** (see
 *     :func:`ageAtBucket`), a sawtooth resetting to zero on every run, which
 *     makes the cadence and the missed run visible at a glance.
 *
 * Neither is hypothetical: ``age_out_last_run_epoch`` is live on the testbed.
 *
 * # Nothing here reads the clock
 *
 * Every function in this module is a **pure function of its arguments**. That
 * is a requirement rather than a style preference, and it is the same argument
 * that put absolute ZULU on the x axis (ruling 3): a value derived from ``now``
 * changes under a pane nobody is touching, so a display left open on a wall
 * board slowly becomes a lie. :func:`ageAtBucket` takes the bucket's own time
 * for exactly this reason.
 *
 * Cross-references:
 *   - REQ-STATUS-075 (units on the axis; the two non-magnitude paths).
 *   - REQ-STATUS-050 / -053 (the catalog and its ``(component_kind,
 *     metric_key)`` keying).
 *   - `design-system.md` → Component patterns → Trend detail chart.
 */
import type { components } from "@/lib/api-types.gen";
import { formatBytes } from "@/system-status/status-connection-format";
import type { HistoryWindow } from "@/system-status/use-component-history";

/**
 * The catalog's presentation-unit vocabulary.
 *
 * Derived from the generated schema rather than re-listed, so a unit added
 * server-side is a compile error here instead of a silent fall-through to the
 * numeric path — which is precisely how a non-magnitude would reach a
 * magnitude axis (CLAUDE.md §4.4).
 */
export type MetricUnit = NonNullable<NonNullable<components["schemas"]["MetricEntry"]["unit"]>>;

/**
 * Which axis a unit can be drawn on.
 *
 * ``magnitude`` is the default and the fall-back: the domain map is
 * open-ended, so an uncatalogued metric has no unit at all and must still plot
 * (REQ-STATUS-054). Defaulting to the numeric axis is safe for that case
 * because the alternative — refusing to plot — hides a metric that is merely
 * unlabelled.
 */
export type UnitKind = "magnitude" | "boolean" | "epoch";

/** Route a unit to the axis that can carry it (REQ-STATUS-075). */
export function unitKind(unit: MetricUnit | undefined | null): UnitKind {
  if (unit === "boolean") return "boolean";
  if (unit === "epoch_seconds") return "epoch";
  return "magnitude";
}

/**
 * Format one value for a y tick, a crosshair reading or a numeric row.
 *
 * ``epoch_seconds`` formats as an absolute ZULU timestamp — the **raw** value,
 * which is what the hover reading shows beside the plotted age. It is
 * deliberately not a magnitude here either: printing `1785729600` in front of
 * an operator is worse than useless.
 *
 * @param value - The measured value, in the unit's own terms.
 * @param unit - The catalogued unit, or ``undefined`` / ``null`` when the
 *   catalog has no entry — which formats as a plain grouped number.
 */
export function formatUnitValue(value: number, unit: MetricUnit | undefined | null): string {
  switch (unit) {
    case "percent":
      return `${trim(value, 1)}%`;
    case "bytes":
      return formatBytes(value);
    case "seconds":
      return formatDuration(value);
    case "milliseconds":
      return value < 1000 ? `${Math.round(value)} ms` : `${trim(value / 1000, 1)} s`;
    case "cores":
      return `${trim(value, 2)} ${value === 1 ? "core" : "cores"}`;
    case "epoch_seconds":
      return formatZuluTimestamp(value);
    case "boolean":
      // A word, not a 1 or a 0: the band's tooltip is read by someone asking
      // "was the lease held?", and `1` answers a different question.
      return value === 0 ? "no" : "yes";
    default:
      return plain(value);
  }
}

/**
 * Format a duration in seconds, keeping the two most significant units.
 *
 * A zero remainder is dropped rather than rendered: ``1h 0m`` reads as a
 * measurement to the minute that happened to land on the hour, where ``1h``
 * reads as what it is.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const parts: readonly (readonly [number, string])[] = [
    [Math.floor(total / 86400), "d"],
    [Math.floor((total % 86400) / 3600), "h"],
    [Math.floor((total % 3600) / 60), "m"],
    [total % 60, "s"],
  ];
  const lead = parts.findIndex(([amount]) => amount > 0);
  if (lead === -1) return "0s";
  const [leadAmount, leadUnit] = parts[lead];
  const next = parts[lead + 1];
  if (next === undefined || next[0] === 0) return `${leadAmount}${leadUnit}`;
  return `${leadAmount}${leadUnit} ${next[0]}${next[1]}`;
}

/**
 * The ``epoch_seconds`` transform: time since the recorded run, at one bucket.
 *
 * # Why an age rather than the timestamp (§9.1)
 *
 * A "last run" timestamp plotted as a magnitude is a staircase climbing to the
 * present: it is honest, and it answers nothing. The age is a **sawtooth that
 * resets to zero on every run**, which makes the two things an operator
 * actually asks visible at a glance — the *cadence* (how often it runs) and the
 * *failure* (a ramp that keeps climbing because it stopped running).
 *
 * # Computed against the BUCKET's time, never against `now`
 *
 * This is the load-bearing guard, and it is why the bucket's timestamp is a
 * parameter rather than something this function could fetch for itself.
 * Against ``now`` the whole series would shift on every refresh and a paused
 * pane would drift — the same staleness failure that moved the x axis off
 * relative ticks (ruling 3).
 *
 * # Two absences, both reported rather than fabricated
 *
 * Returns ``null`` — a **gap**, per REQ-STATUS-073 — rather than a number in
 * two cases where a number would be a false statement:
 *
 *   - **The value is ahead of its bucket.** Emitter/collector clock skew. A
 *     negative age is not a measurement, and clamping to zero would assert
 *     "it just ran", which is a plausible reading an operator would believe
 *     (CLAUDE.md §8.1 — fail safe, never fail plausible).
 *   - **The value is the zero sentinel.** A zero epoch means *never ran*, not
 *     *ran in 1970*. Plotted as an age it is ~56 years, which flattens every
 *     real point in the window against the axis.
 *
 * @param bucketTimeSeconds - The bucket's own timestamp, epoch seconds.
 * @param epochValue - The recorded run time, epoch seconds.
 * @returns Age in seconds, or ``null`` where no age can be stated.
 */
export function ageAtBucket(bucketTimeSeconds: number, epochValue: number): number | null {
  if (epochValue <= 0) return null;
  const age = bucketTimeSeconds - epochValue;
  return age < 0 ? null : age;
}

/**
 * An x-axis tick: absolute ZULU, at the window's resolution (ruling 3).
 *
 * The short windows carry ``HH:MM``; the windows that can span a date boundary
 * carry the date too, so a tick at ``23:50`` and one at ``00:10`` cannot be
 * read as ten minutes apart in the wrong direction.
 *
 * ⚠ **Never a relative offset.** ``−6h`` means something different ten minutes
 * later, and this panel is left open on wall displays for hours. Absolute time
 * also correlates with the top-bar clock and with an operator quoting a time to
 * someone else. The cost is ~3× the label width, which is what makes the
 * responsive tick rule (REQ-STATUS-078) load-bearing rather than a nicety.
 */
export function formatZuluTick(epochSeconds: number, window: HistoryWindow): string {
  const at = new Date(epochSeconds * 1000);
  const hhmm = `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}Z`;
  if (window === "1h" || window === "6h") return hhmm;
  return `${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())} ${hhmm}`;
}

/**
 * The precise reading behind a crosshair or a keyboard cursor: ZULU, to the
 * second.
 *
 * This is where ruling 3's absolute time is read exactly, so it keeps full
 * precision where the tick drops to the minute.
 */
export function formatZuluTimestamp(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Two-digit zero pad for a ZULU field. */
function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** A grouped number, at most two decimals — the no-unit fall-back. */
function plain(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** Round to at most ``places`` decimals, without a trailing ``.0``. */
function trim(value: number, places: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: places });
}
